import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, Chip, IconButton, ListItemText, MenuItem, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import { getReservationHistory, getReservationHistoryBatch } from '../../api/autoAttendance';
import { getPointTrainers } from '../../api/staff';
import { errorMessages } from '../../utils/methods';
import {
  bodyCellSx, CLASS_TYPE_OPTIONS, dateFieldSx, headCellSx, SEARCH_FIELDS, searchButtonSx,
} from './classManageShared';

// 수업 관리 > 이력. 예약·출결·취소를 누가, 어디서, 단건/일괄/자동 중 무엇으로 했는지. 일괄은 한 줄 + 펼치기
const PAGE_SIZE = 50;
const DATE_FORMAT = 'YYYY-MM-DD';
const MAX_PERIOD_MONTHS = 6;

const ACTION_META = {
  RESERVE: { label: '예약', color: '#1565C0', background: '#E3F2FD' },
  WAIT: { label: '대기', color: '#6B7280', background: '#F2F2F2' },
  PROMOTE: { label: '대기→예약', color: '#5B6BC0', background: '#ECEEFA' },
  ATTEND: { label: '출석', color: '#1E7E78', background: '#E3F4F2' },
  ABSENT: { label: '결석', color: '#C7362A', background: '#FDECEA' },
  RESET: { label: '출결 취소', color: '#B45309', background: '#FFF3E0' },
  CANCEL: { label: '예약취소', color: '#C7362A', background: '#FDECEA' },
};
const STATUS_LABEL = {
  RESERVED: '예약중', ATTENDED: '출석', ABSENT: '결석', CANCELED: '취소', WAITING: '대기',
};
const statusChangeText = row => {
  if (!row.fromStatus && row.toStatus === 'RESERVED') return '신규 예약';
  return `${STATUS_LABEL[row.fromStatus] || '-'} → ${row.toStatus ? STATUS_LABEL[row.toStatus] : '삭제'}`;
};
const SOURCE_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'CRM', label: 'CRM' },
  { value: 'MEMBER_APP', label: '회원앱' },
  { value: 'TRAINER_APP', label: '강사앱' },
  { value: 'KIOSK', label: '키오스크' },
  { value: 'SCHEDULER', label: '자동' },
];
const SOURCE_LABEL = Object.fromEntries(SOURCE_OPTIONS.filter(o => o.value).map(o => [o.value, o.label]));
const MODE_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'SINGLE', label: '단건' },
  { value: 'BATCH', label: '일괄' },
  { value: 'AUTO', label: '자동' },
];
const MODE_LABEL = Object.fromEntries(MODE_OPTIONS.filter(o => o.value).map(o => [o.value, o.label]));
const ACTOR_KIND_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'STAFF', label: '직원' },
  { value: 'MEMBER', label: '회원' },
  { value: 'AUTO', label: '자동' },
];
const ACTOR_KIND_LABEL = { STAFF: '직원', MEMBER: '회원', AUTO: '자동' };

const defaultFilter = () => ({
  startDate: dayjs().subtract(7, 'day').format(DATE_FORMAT),
  endDate: dayjs().format(DATE_FORMAT),
  actions: [],
  actorKind: '',
  actorEmployeeId: '',
  source: '',
  mode: '',
  classType: '',
  member: '',
  className: '',
});

function ActionBadge({ action, count, mixed }) {
  const meta = ACTION_META[action] || { label: action, color: '#555', background: '#F2F2F2' };
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block', px: '8px', py: '2px', borderRadius: '10px', fontSize: '12px', fontWeight: 600,
        color: meta.color, background: meta.background, whiteSpace: 'nowrap',
      }}>
      {count > 1 ? `일괄 ${mixed ? '처리' : meta.label} ${count}건` : meta.label}
    </Box>
  );
}

const actorText = row => {
  if (row.actorKind === 'AUTO') return '자동';
  if (row.actorKind === 'MEMBER') return row.actorName || row.memberName || '회원';
  return row.actorName || '-';
};

const classText = row =>
  [row.classDate, row.startTime && `${row.startTime}~${row.endTime || ''}`].filter(Boolean).join(' ');

function HistoryRow({ row, branchId, token, nested }) {
  const [open, setOpen] = useState(false);
  const isBatch = row.count > 1;
  const { data: children = [], isLoading } = useQuery({
    queryKey: ['reservationHistoryBatch', branchId, row.batchId],
    queryFn: () => getReservationHistoryBatch(branchId, row.batchId, token),
    enabled: open && isBatch && !!row.batchId,
  });
  return (
    <>
      <TableRow hover sx={nested ? { background: '#FAFBFC' } : undefined}>
        <TableCell align="center" sx={{ ...bodyCellSx, width: 40, px: 0 }}>
          {isBatch && (
            <IconButton size="small" onClick={() => setOpen(v => !v)} aria-label={open ? '묶음 접기' : '묶음 펼치기'}>
              {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell align="center" sx={bodyCellSx}>{row.regDate}</TableCell>
        <TableCell align="center" sx={bodyCellSx}>
          <ActionBadge action={row.action} count={row.count} mixed={row.mixedAction} />
        </TableCell>
        <TableCell align="center" sx={{ ...bodyCellSx, color: '#666' }}>
          {isBatch ? '-' : statusChangeText(row)}
        </TableCell>
        <TableCell align="center" sx={bodyCellSx}>{isBatch ? `${row.memberName || ''} 외` : row.memberName || '-'}</TableCell>
        <TableCell align="center" sx={bodyCellSx}>{classText(row)}</TableCell>
        <TableCell align="center" sx={bodyCellSx}>{row.classTypeLabel}</TableCell>
        <TableCell align="center" sx={bodyCellSx}>{row.className || '-'}</TableCell>
        <TableCell align="center" sx={bodyCellSx}>{row.trainerName || '-'}</TableCell>
        <TableCell align="center" sx={bodyCellSx}>
          {actorText(row)}
          <Typography component="span" sx={{ fontSize: '11px', color: '#999', ml: '4px' }}>
            {ACTOR_KIND_LABEL[row.actorKind]}
          </Typography>
        </TableCell>
        <TableCell align="center" sx={bodyCellSx}>{SOURCE_LABEL[row.source] || row.source}</TableCell>
        <TableCell align="center" sx={bodyCellSx}>{MODE_LABEL[row.mode] || row.mode}</TableCell>
      </TableRow>
      {open && isLoading && (
        <TableRow><TableCell colSpan={12} align="center" sx={{ color: '#888' }}>불러오는 중...</TableCell></TableRow>
      )}
      {open && children.map(child => (
        <HistoryRow key={child.id} row={child} branchId={branchId} token={token} nested />
      ))}
    </>
  );
}

function ReservationHistory({ branchId }) {
  const user = useSelector(state => state.user.user);
  const toast = useToast();
  const [form, setForm] = useState(defaultFilter);
  const [query, setQuery] = useState(defaultFilter);
  const [searchField, setSearchField] = useState('member');
  const [searchText, setSearchText] = useState('');
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);

  const { data: trainers = [] } = useQuery({
    queryKey: ['unconfirmedTrainers', branchId],
    queryFn: () => getPointTrainers(branchId, user.token),
    enabled: !!branchId && !!user?.token,
  });

  const { data, isLoading, isError, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['reservationHistory', branchId, query],
      initialPageParam: 0,
      queryFn: ({ pageParam }) =>
        getReservationHistory({ branchId, ...query, page: pageParam, size: PAGE_SIZE }, user.token),
      getNextPageParam: lastPage =>
        lastPage && lastPage.page + 1 < lastPage.totalPages ? lastPage.page + 1 : undefined,
      enabled: !!branchId && !!user?.token,
    });

  const rows = useMemo(() => (data?.pages || []).flatMap(p => p?.content || []), [data]);
  const totalElements = data?.pages?.[0]?.totalElements ?? 0;

  useEffect(() => {
    const target = sentinelRef.current;
    if (!target || !hasNextPage) return undefined;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && !isFetchingNextPage) fetchNextPage();
      },
      { root: scrollRef.current, rootMargin: '200px' },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, rows.length]);

  const runSearch = next => {
    if (next.startDate > next.endDate) {
      toast.error('시작일이 종료일보다 늦을 수 없습니다.');
      return;
    }
    if (dayjs(next.endDate).isAfter(dayjs(next.startDate).add(MAX_PERIOD_MONTHS, 'month'))) {
      toast.error(`조회 기간은 최대 ${MAX_PERIOD_MONTHS}개월입니다.`);
      return;
    }
    if (JSON.stringify(next) === JSON.stringify(query)) refetch();
    setForm(next);
    setQuery(next);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  const addSearchChip = () => {
    const text = searchText.trim();
    if (!text) return;
    setSearchText('');
    runSearch({ ...form, [searchField]: text });
  };

  const setField = key => e => setForm(prev => ({ ...prev, [key]: e.target.value }));

  return (
    <Box sx={{ py: 2, display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px' }}>
        <TextField
          type="date" size="small" label="처리일 시작" InputLabelProps={{ shrink: true }} sx={dateFieldSx}
          value={form.startDate} onChange={setField('startDate')}
        />
        <TextField
          type="date" size="small" label="처리일 종료" InputLabelProps={{ shrink: true }} sx={dateFieldSx}
          value={form.endDate} onChange={setField('endDate')}
        />
        <TextField
          select size="small" label="동작" sx={{ minWidth: 130, maxWidth: 220 }}
          SelectProps={{
            multiple: true,
            renderValue: selected => (selected.length ? selected.map(a => ACTION_META[a]?.label).join(', ') : '전체'),
            displayEmpty: true,
          }}
          InputLabelProps={{ shrink: true }}
          value={form.actions}
          onChange={e => setForm(prev => ({ ...prev, actions: e.target.value }))}>
          {Object.entries(ACTION_META).map(([value, meta]) => (
            <MenuItem key={value} value={value} dense>
              <Checkbox size="small" checked={form.actions.includes(value)} sx={{ p: '2px', mr: 1 }} />
              <ListItemText primary={meta.label} />
            </MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="처리자" sx={{ minWidth: 100 }} value={form.actorKind} onChange={setField('actorKind')}>
          {ACTOR_KIND_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="처리 직원" sx={{ minWidth: 130 }}
          value={form.actorEmployeeId} onChange={setField('actorEmployeeId')}>
          <MenuItem value="">전체</MenuItem>
          {trainers.map(t => <MenuItem key={t.trainerId} value={t.trainerId}>{t.trainerName}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="처리 방식" sx={{ minWidth: 100 }} value={form.mode} onChange={setField('mode')}>
          {MODE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="출처" sx={{ minWidth: 100 }} value={form.source} onChange={setField('source')}>
          {SOURCE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="수업유형" sx={{ minWidth: 120 }} value={form.classType} onChange={setField('classType')}>
          {CLASS_TYPE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="검색 대상" sx={{ minWidth: 110 }}
          value={searchField} onChange={e => setSearchField(e.target.value)}>
          {SEARCH_FIELDS.map(f => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
        </TextField>
        <TextField
          size="small" label="검색어" sx={{ minWidth: 160 }}
          placeholder={SEARCH_FIELDS.find(f => f.value === searchField)?.placeholder}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) addSearchChip();
          }}
        />
        <Button variant="outlined" onClick={() => (searchText.trim() ? addSearchChip() : runSearch(form))} sx={searchButtonSx}>
          조회
        </Button>
        {SEARCH_FIELDS.filter(f => query[f.value]).map(f => (
          <Chip
            key={f.value} size="small" label={`${f.label}: ${query[f.value]}`}
            onDelete={() => runSearch({ ...form, [f.value]: '' })}
            sx={{ height: 30, fontSize: '13px', background: '#F2F5F6', border: '1px solid #E4E9EA' }}
          />
        ))}
      </Box>

      <Typography sx={{ fontSize: '14px', color: '#555' }}>
        전체 {totalElements.toLocaleString()}건
        <Typography component="span" sx={{ fontSize: '12px', color: '#999', ml: 1 }}>
          (일괄 처리는 한 줄로 묶여 있고 ▼ 로 펼칩니다)
        </Typography>
      </Typography>

      <TableContainer
        ref={scrollRef}
        sx={{ border: '1px solid #E8E8E8', borderRadius: '8px', overflowY: 'auto', maxHeight: 'calc(100vh - 300px)' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['', '처리일시', '동작', '변경', '회원', '수업 일시', '수업유형', '수업명', '강사', '처리자', '출처', '방식'].map(h => (
                <TableCell key={h || 'expand'} align="center" sx={headCellSx}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow><TableCell colSpan={12} align="center">불러오는 중...</TableCell></TableRow>
            )}
            {isError && (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ color: '#d32f2f' }}>{errorMessages(error)}</TableCell>
              </TableRow>
            )}
            {!isLoading && !isError && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 6, color: '#888' }}>조회된 이력이 없습니다.</TableCell>
              </TableRow>
            )}
            {rows.map(row => (
              <HistoryRow key={row.id} row={row} branchId={branchId} token={user.token} />
            ))}
          </TableBody>
        </Table>
        <Box ref={sentinelRef} sx={{ height: '1px' }} />
        {isFetchingNextPage && (
          <Typography sx={{ textAlign: 'center', py: 1.5, fontSize: '13px', color: '#888' }}>불러오는 중...</Typography>
        )}
      </TableContainer>
    </Box>
  );
}

export default ReservationHistory;
